import React, { SyntheticEvent, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import useIntegration from 'api/useIntegration';
import useSections from 'api/useSections';
import useTheme from 'api/useTheme';
import useUsers from 'api/useUsers';
import { AxiosError } from 'axios';
import { EPublisherSolutionModel, ETaskId } from 'common/contracts';
import {
  EPermissionAction,
  EPermissionGroup,
  IPermissions
} from 'common/permissions.types';
import { EButtonColor, EFeatureFlag, ELocalStorageKeys } from 'constants/enums';
import { TabsPanel } from 'design-system/TabPanel/TabPanel';
import { Form, Formik, FormikConfig } from 'formik';
import { ERouteContext } from 'routes/types';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { uiActions } from 'store/uiSlice';
import { localStorageUtil } from 'utils/localStorageUtil';
import { permissionsUtil } from 'utils/permissionsUtil';

import DialogModal from 'components/Dialog/Dialog';
import PageTopBar from 'components/Topbar/PageTopBar';

import { EToasterStatus, showToast } from '../../utils/showToast';

import { CheckoutThemeFormValues } from './CheckoutTheme/types';
import { useGetCheckoutInitialValues } from './CheckoutTheme/useGetCheckoutInitialValues';
import { CompletedThemeFormValues } from './CompletedTheme/types';
import { useGetCompletedInitialValues } from './CompletedTheme/useGetCompletedInitialValues';
import { completedValidationSchema } from './CompletedTheme/validation-schema';
import { GeneralThemeFormValues } from './GeneralSettings/generalTheme.types';
import { useGetGeneralInitialValues } from './GeneralSettings/useGetGeneralInitialValues';
import { generalSchema } from './GeneralSettings/validation-schema';
import { LoginThemeFormValues } from './LoginTheme/types';
import { useGetLoginInitialValues } from './LoginTheme/useGetLoginInitialValues';
import { loginValidationSchema } from './LoginTheme/validation-schema';
import {
  SectionsItem,
  StoreThemeFormValues
} from './StoreTheme/storeTheme.types';
import { useGetStoreInitialValues } from './StoreTheme/useGetStoreInitialValues';
import { storeSchema } from './StoreTheme/validation-schema';

import './style.scss';

enum EStoreState {
  GENERAL = 'general',
  STORE = 'store',
  LOGIN = 'login',
  ACCOUNT_APPROVAL_MODAL = 'account-approval-modal',
  COMPLETED = 'completed',
  CHECKOUT = 'checkout'
}

export const ThemeView: React.FC<{ context: ERouteContext }> = ({
  context
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { publisherId } = useParams();
  const { setTaskStatus, hasCategories } = useChecklistState();

  const initialPath = useMemo(
    () => location.pathname.split('/').filter(Boolean).pop(),
    []
  );

  const initialTab = useMemo(
    () =>
      Object.values(EStoreState).includes(initialPath as EStoreState)
        ? (initialPath as EStoreState)
        : EStoreState.GENERAL,
    []
  );

  const isCheckoutUser =
    localStorageUtil.get(ELocalStorageKeys.PUBLISHER_SOLUTION_MODEL) ===
    EPublisherSolutionModel.CHECKOUT;

  const [tab, setTab] = useState<EStoreState>(
    isCheckoutUser ? EStoreState.CHECKOUT : initialTab
  );
  const [displayUnsavedDialog, setDisplayUnsavedDialog] = useState(false);
  const [navigationAction, setNavigationAction] = useState<(() => void) | null>(
    null
  );

  const {
    getGeneralTheme: generalTheme,
    updateGeneralTheme,
    getStoreScreen: storeScreen,
    updateStoreScreen,
    getLoginTheme: loginTheme,
    updateLoginTheme,
    getCheckoutTheme: checkoutTheme,
    updateCheckoutTheme,
    getCompletedTheme: completeTheme,
    updateCompletedTheme
  } = useTheme(publisherId);

  // getGeneralTheme.refetch();
  const { updateIntegration } = useIntegration(publisherId);
  const { getSections, upsertSections } = useSections(publisherId);
  const dispatch = useDispatch();
  const [isCheckoutDialogOpen, setIsCheckoutDialogOpen] = useState(false);
  const { fetchFeatureFlags } = useUsers({ publisherId });

  const { initialValues: generalInitialValues } = useGetGeneralInitialValues();
  const { initialValues: storeInitialValues } = useGetStoreInitialValues();
  const { initialValues: loginInitialValues } = useGetLoginInitialValues();
  const { initialValues: checkoutInitialValues } =
    useGetCheckoutInitialValues();
  const { initialValues: completedInitialValues } =
    useGetCompletedInitialValues();

  const tabConfig: Record<EStoreState, FormikConfig<any>> = useMemo(
    () => ({
      [EStoreState.GENERAL]: {
        initialValues: generalInitialValues,
        validationSchema: generalSchema,
        onSubmit: async (
          values: GeneralThemeFormValues,
          { resetForm, setSubmitting }
        ) => {
          try {
            await updateGeneralTheme.mutateAsync(values);
            dispatch(uiActions.setUnsavedChanges(false));
            setSubmitting(false);

            showToast({
              message: 'General theme updated',
              status: EToasterStatus.SUCCESS
            });

            await generalTheme.refetch();

            resetForm({ values });
          } catch (error) {
            showToast({
              message: 'Error updating general theme',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                )?.message || 'Unknown error occurred'
            });
          }
        }
      },
      [EStoreState.STORE]: {
        initialValues: storeInitialValues,
        validationSchema: storeSchema,
        onSubmit: async (
          values: StoreThemeFormValues,
          { resetForm, setSubmitting, setFieldValue }
        ) => {
          try {
            const sectionsValues = values.sections.map(
              (value: SectionsItem) => {
                const { _id, ...rest } = value;
                return rest;
              }
            ) as SectionsItem[];

            const filteredSectionsData = sectionsValues.filter(
              (item) => !item.toBeDeleted
            );

            const newSectionsData = filteredSectionsData.map((section) => {
              const sectionData: Partial<SectionsItem> = {
                name: section.name,
                publisherSectionId: section.publisherSectionId
              };
              if (section.image) {
                sectionData.image = section.image;
              }
              if (section.sectionId) {
                sectionData.sectionId = section.sectionId;
              }
              return sectionData;
            });

            const storeScreenValues = {
              defaultBanner: values.defaultBanner,
              addToHomeScreen: values.addToHomeScreen,
              backToGameButton: values.backToGameButton,
              header: values.header,
              noOffersTitleText: values.noOffersTitleText,
              noOffersMessageText: values.noOffersMessageText,
              bundleBorderColor: values.bundleBorderColor,
              buttonColor: values.buttonColor,
              buttonTextColor: values.buttonTextColor,
              defaultPlayerImage: values.defaultPlayerImage
            };

            await upsertSections.mutateAsync(newSectionsData);
            await updateStoreScreen.mutateAsync(storeScreenValues);

            dispatch(uiActions.setUnsavedChanges(false));
            await getSections.refetch();
            setSubmitting(false);

            showToast({
              message: 'Store page updated',
              status: EToasterStatus.SUCCESS
            });

            await storeScreen.refetch();

            resetForm({ values });
          } catch (error) {
            showToast({
              message: 'Error updating store theme',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                ).message || 'Unknown error occurred'
            });
            if (getSections.data?.result.length > 0) {
              await setFieldValue('sections', getSections.data?.result);
            }
          }
        }
      },
      [EStoreState.LOGIN]: {
        initialValues: loginInitialValues,
        validationSchema: loginValidationSchema,
        onSubmit: async (
          values: LoginThemeFormValues,
          { resetForm, setSubmitting }
        ) => {
          try {
            await updateLoginTheme.mutateAsync(values);
            dispatch(uiActions.setUnsavedChanges(false));
            setSubmitting(false);

            showToast({
              message: 'Login theme updated',
              status: EToasterStatus.SUCCESS
            });

            await loginTheme.refetch();

            resetForm({ values });
          } catch (error) {
            showToast({
              message: 'Error updating login theme',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                ).message || 'Unknown error occurred'
            });
          }
        }
      },
      [EStoreState.ACCOUNT_APPROVAL_MODAL]: {
        initialValues: loginInitialValues,
        validationSchema: loginValidationSchema,
        onSubmit: async (
          values: LoginThemeFormValues,
          { resetForm, setSubmitting }
        ) => {
          try {
            await updateLoginTheme.mutateAsync(values);
            dispatch(uiActions.setUnsavedChanges(false));
            setSubmitting(false);

            showToast({
              message: 'Account approval modal updated',
              status: EToasterStatus.SUCCESS
            });

            await loginTheme.refetch();

            resetForm({ values });
          } catch (error) {
            showToast({
              message: 'Error updating account approval modal',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                ).message || 'Unknown error occurred'
            });
          }
        }
      },
      [EStoreState.CHECKOUT]: {
        initialValues: checkoutInitialValues,
        onSubmit: async (
          values: CheckoutThemeFormValues,
          { setSubmitting }
        ) => {
          try {
            values.publisherId =
              publisherId ||
              localStorageUtil.get(ELocalStorageKeys.PUBLISHER_ID) ||
              '';
            if (values?.mobileBgImage && values?.mobileBgImage === 'none') {
              values.mobileBgImage = '';
            }

            await updateCheckoutTheme.mutateAsync(values);

            dispatch(uiActions.setUnsavedChanges(false));
            setSubmitting(false);

            showToast({
              message: 'Checkout theme updated',
              status: EToasterStatus.SUCCESS
            });
            setIsCheckoutDialogOpen(true);

            if (hasCategories) {
              setTaskStatus(ETaskId.EDIT_CHECKOUT_IN_BUILDER);
            }

            await checkoutTheme.refetch();
            await generalTheme.refetch();
          } catch (error) {
            showToast({
              message: 'Error updating checkout theme',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                ).message || 'Unknown error occurred'
            });
          }
        }
      },
      [EStoreState.COMPLETED]: {
        initialValues: completedInitialValues,
        validationSchema: completedValidationSchema,
        onSubmit: async (
          values: CompletedThemeFormValues,
          { resetForm, setSubmitting }
        ) => {
          try {
            const integrationValues = {
              deepLinks: [
                {
                  platform: 'ios',
                  deepLink: values.deepLinks.ios
                },
                {
                  platform: 'android',
                  deepLink: values.deepLinks.android
                },
                {
                  platform: 'web',
                  deepLink: values.deepLinks.web
                }
              ]
            };

            await updateCompletedTheme.mutateAsync(values);
            await updateIntegration.mutateAsync(integrationValues);

            dispatch(uiActions.setUnsavedChanges(false));
            setSubmitting(false);

            showToast({
              message: 'Completed page updated',
              status: EToasterStatus.SUCCESS
            });

            await completeTheme.refetch();

            resetForm({ values });
          } catch (error) {
            showToast({
              message: 'Error updating completed page',
              status: EToasterStatus.ERROR,
              description:
                (
                  (error as AxiosError).response?.data as {
                    message: string;
                  }
                ).message || 'Unknown error occurred'
            });
          }
        }
      }
    }),
    [
      generalInitialValues,
      storeInitialValues,
      loginInitialValues,
      checkoutInitialValues,
      completedInitialValues
    ]
  );

  const { initialValues, validationSchema, onSubmit }: FormikConfig<any> =
    useMemo(() => tabConfig[tab], [tab, tabConfig, location]);

  useEffect(() => {
    const segments = location.pathname.split('/');
    segments[segments.length - 1] = tab;
    const newPath = segments.join('/');
    navigate(newPath);
  }, [tab]);

  // Add new effect to redirect if path ends with /theme
  useEffect(() => {
    if (location.pathname.endsWith('/theme')) {
      navigate(`${location.pathname}/${tab}`, { replace: true });
    }
  }, [location.pathname, navigate, tab]);

  const shouldAdminTabsBeShown: boolean = useMemo(
    () =>
      Boolean(
        permissionsUtil.isSuperAdminByProject(publisherId || '') ||
          permissionsUtil.isAdminByProject(publisherId || '') ||
          permissionsUtil.canAccessBuilder(
            localStorageUtil.get<IPermissions>(ELocalStorageKeys.PERMISSIONS) ||
              {},
            publisherId || ''
          )
      ),
    [publisherId]
  );

  const hasDeeplinkOfferFeatureFlag = useMemo(
    () =>
      fetchFeatureFlags.data?.featureFlags?.[
        EFeatureFlag.DASHBOARD_GENERATE_DEEPLINK
      ],
    [fetchFeatureFlags.data]
  );

  const tabs = useMemo(() => {
    return [
      {
        label: 'General Settings',
        value: EStoreState.GENERAL,
        shouldHide: isCheckoutUser
      },
      {
        label: 'Store Page',
        value: EStoreState.STORE,
        shouldHide: isCheckoutUser
      },
      {
        label: 'Login Page',
        value: EStoreState.LOGIN,
        shouldHide:
          isCheckoutUser || context === ERouteContext.PORTAL_LEVEL_STORE
      },
      {
        label: 'Checkout Page',
        value: EStoreState.CHECKOUT,
        shouldHide: !shouldAdminTabsBeShown
      },
      {
        label: 'Completed Page',
        value: EStoreState.COMPLETED,
        shouldHide: isCheckoutUser
      },
      {
        label: 'Account Approval Modal',
        value: EStoreState.ACCOUNT_APPROVAL_MODAL,
        shouldHide: isCheckoutUser || !hasDeeplinkOfferFeatureFlag
      }
    ];
  }, [
    shouldAdminTabsBeShown,
    isCheckoutUser,
    hasDeeplinkOfferFeatureFlag,
    context
  ]);

  const handleTabChange = (newTab: EStoreState) => {
    setTab(newTab);
    const newPath = `/${context === ERouteContext.PORTAL_LEVEL_STORE ? 'store' : 'project'}/${publisherId}/theme/${newTab}`;
    navigate(newPath);
  };

  const handleNavigationAttempt = (newTab: EStoreState, dirty: boolean) => {
    if (!dirty) return handleTabChange(newTab);
    setDisplayUnsavedDialog(true);
    setNavigationAction(() => () => handleTabChange(newTab));
  };

  return (
    <Formik
      key={tab}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ isValid, dirty, isSubmitting, submitForm }) => {
        return (
          <Form className="storeTheme-form">
            <TabsPanel
              tabs={tabs}
              headerComponent={
                <PageTopBar
                  withTabsDesign={true}
                  disable={false}
                  headline="Builder"
                  buttons={[
                    {
                      text: 'Save',
                      action: submitForm,
                      disabled:
                        !permissionsUtil.canUserEdit() ||
                        !isValid ||
                        !dirty ||
                        isSubmitting,
                      hidden: !permissionsUtil.isActionEnabled(
                        null,
                        EPermissionGroup.BUILDER,
                        EPermissionAction.MANAGE,
                        publisherId
                      )
                    }
                  ]}
                />
              }
              handleTabChange={(event: SyntheticEvent, newValue: EStoreState) =>
                handleNavigationAttempt(newValue, dirty)
              }
              activeTabVal={tab}
            />
            <DialogModal
              headline="Please note"
              isOpen={isCheckoutDialogOpen}
              text="Your changes have been saved. Please allow a few minutes for them to take effect."
              buttons={[
                {
                  text: 'Got it',
                  color: EButtonColor.PRIMARY,
                  variant: 'contained',
                  func: () => setIsCheckoutDialogOpen(false)
                }
              ]}
            />
            <DialogModal
              isOpen={displayUnsavedDialog}
              headline="Unsaved Changes"
              text="Are you sure you want to switch tabs without saving your changes?"
              buttons={[
                {
                  text: 'Stay',
                  color: EButtonColor.SECONDARY,
                  variant: 'outlined',
                  func: () => {
                    setDisplayUnsavedDialog(false);
                  }
                },
                {
                  text: 'Continue',
                  color: EButtonColor.ERROR,
                  variant: 'contained',
                  func: () => {
                    setDisplayUnsavedDialog(false);
                    navigationAction?.();
                  }
                }
              ]}
              closeDialog={() => setDisplayUnsavedDialog(false)}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
